import React from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Col, Row, Form, Container, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import branchApi from "../../../services/branchApi";
import api from "../../../services/httpClient";
import "../../../assets/styles/users.scss";
import "../../../assets/styles/createUserForm.scss";

const initialFormData = {
  isActive: "",
  branchIds: [],
  login: "",
  email: "",
  fullName: "",
  role: "",
};

const CreateUserForms = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = React.useState([]);
  const [formData, setFormData] = React.useState(initialFormData);
  const [saving, setSaving] = React.useState(false);

  const normalizedBranches = Array.isArray(branches)
    ? branches
        .map((branch) => {
          if (!branch) return null;
          if (typeof branch === "string") {
            return { id: branch, name: branch };
          }

          if (typeof branch === "object") {
            return {
              id: branch.id ?? branch.Id,
              name:
                branch.name ??
                branch.Name ??
                String(branch.id ?? branch.Id ?? ""),
            };
          }

          return null;
        })
        .filter((branch) => branch?.id != null)
    : [];

  React.useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const list = await branchApi.getAllBranches(controller.signal);
        setBranches(Array.isArray(list) ? list : []);
      } catch {
        setBranches([]);
      }
    })();

    return () => controller.abort();
  }, []);

  const handleReset = (event) => {
    event.preventDefault();

    setFormData(initialFormData);
  };

  const handleFieldChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleBranchChange = (event) => {
    const selectedValues = Array.from(
      event.target.selectedOptions,
      (option) => option.value,
    );

    setFormData((prev) => ({
      ...prev,
      branchIds: selectedValues,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      isActive: formData.isActive === "" ? null : Number(formData.isActive),
      userRights: formData.role,
      login: formData.login.trim(),
      name: formData.fullName.trim(),
      email: formData.email.trim(),
      branchIds: formData.branchIds.map((id) => Number(id)),
    };

    try {
      setSaving(true);
      await api.post("/User/Create", payload);
      alert("Użytkownik został utworzony.");
      navigate("/administration/users");
    } catch (error) {
      console.error("Error creating user:", error);
      if (error?.response?.status === 423) {
        alert("Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana");
      } else {
        alert("Wystąpił błąd podczas tworzenia użytkownika.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid="md" className="create-user-form">
      <div className="create-user-form__card">
        <div className="create-user-form__header mb-4">
          <div>
            <h4 className="create-user-form__title">Nowy użytkownik</h4>
            <p className="create-user-form__subtitle">
              Uzupełnij dane użytkownika i wybierz przypisany oddział oraz rolę.
              Pamiętaj, aby użytkownik mógł się zalogować wymagane jest
              przypisanie w Azure Active Directory.
            </p>
          </div>
        </div>

        <Form
          className="create-user-form__body"
          onReset={handleReset}
          onSubmit={handleSubmit}
        >
          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelect1"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Aktywny
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Select
                className="create-user-form__control"
                value={formData.isActive}
                onChange={handleFieldChange("isActive")}
              >
                <option value={null}>---Wybierz---</option>
                <option value={1}>Tak</option>
                <option value={0}>Nie</option>
              </Form.Select>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelect2"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Oddział DHL [EX DR]
            </Form.Label>
            <Col lg={8} sm={12}>
              <OverlayTrigger
                overlay={
                  <Tooltip id="branches-tooltip">
                    Przytrzymaj CTRL+C aby wybrać wiele.

                  </Tooltip>
                }
              >
                <Form.Select
                  className="create-user-form__control"
                  multiple
                  value={formData.branchIds}
                  onChange={handleBranchChange}
                >
                  {normalizedBranches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </Form.Select>
              </OverlayTrigger>
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput1"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Login
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="text"
                placeholder="Wprowadź login"
                className="create-user-form__control"
                value={formData.login}
                onChange={handleFieldChange("login")}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput2"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Email
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="email"
                placeholder="Wprowadź adres email"
                className="create-user-form__control"
                value={formData.email}
                onChange={handleFieldChange("email")}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formInput3"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Imię i Nazwisko
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Control
                type="text"
                placeholder="Wprowadź imię i nazwisko"
                className="create-user-form__control"
                value={formData.fullName}
                onChange={handleFieldChange("fullName")}
              />
            </Col>
          </Form.Group>

          <Form.Group
            as={Row}
            className="create-user-form__group"
            controlId="formSelectRole"
          >
            <Form.Label
              column
              lg={4}
              sm={12}
              className="create-user-form__label"
            >
              Rola
            </Form.Label>
            <Col lg={8} sm={12}>
              <Form.Select
                className="create-user-form__control"
                value={formData.role}
                onChange={handleFieldChange("role")}
              >
                <option value="">---Wybierz---</option>
                <option value="ROLE_USER">User</option>
                <option value="ROLE_SUPERUSER">Power User</option>
              </Form.Select>
            </Col>
          </Form.Group>

          <Row className="create-user-form__actions">
            <Col sm="auto" className="create-user-form__actions-col">
              <button
                type="reset"
                className="btn create-user-form__btn create-user-form__btn--reset"
              >
                Reset
              </button>
              <button
                type="submit"
                className="btn create-user-form__btn create-user-form__btn--save"
                disabled={saving}
              >
                {saving ? "Zapisywanie..." : "Zapisz"}
              </button>
            </Col>
          </Row>
        </Form>
      </div>
    </Container>
  );
};

export default CreateUserForms;
